/**
 * AIMDv2 – AddTextModal
 *
 * Modal dialog for adding a text-based document to the program.
 * Fields: Title, Insert text, and a helper note.
 * Uses DosenConfirmModal as the base shell and DosenInput for fields.
 */

import React, { useState } from 'react';
import { Box, Typography } from '@mui/material';
import DosenConfirmModal from '../../../components/DosenConfirmModal';
import DosenInput from '../../../components/DosenInput';
import DosenButton from '../../../../../components/ui/Buttons/DosenButton';

interface AddTextModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (title: string, textContent: string) => void;
}

const AddTextModal: React.FC<AddTextModalProps> = ({ open, onClose, onSubmit }) => {
  const [title, setTitle] = useState('');
  const [textContent, setTextContent] = useState('');

  const isValid = title.trim().length > 0 && textContent.trim().length > 0;

  const handleSubmit = () => {
    if (!isValid) return;
    onSubmit(title.trim(), textContent.trim());
    setTitle('');
    setTextContent('');
    onClose();
  };

  const handleClose = () => {
    setTitle('');
    setTextContent('');
    onClose();
  };

  return (
    <DosenConfirmModal open={open} onClose={handleClose} title="Add text to this Program">
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '20px', py: 1 }}>
        <DosenInput
          label="Title*"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Enter a title for this text..."
        />

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          <DosenInput
            label="Text*"
            value={textContent}
            onChange={(e) => setTextContent(e.target.value)}
            placeholder="Type or paste text here..."
            multiline
            rows={4}
          />
          <Typography
            sx={{
              fontSize: 12,
              fontFamily: 'Inter',
              color: 'var(--Text-text-secondary, #B6B6B6)',
              lineHeight: '16px'
            }}
          >
            Add text (by either typing now or by pasting from other sources like Notion)
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <DosenButton
            tone="cta"
            sizeVariant="sm"
            onClick={handleSubmit}
            disabled={!isValid}
            sx={{ minWidth: 192 }}
          >
            Add Text
          </DosenButton>
        </Box>
      </Box>
    </DosenConfirmModal>
  );
};

export default AddTextModal;
